package dev.wyns.ventrova.ui.components

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.wyns.ventrova.ui.theme.VentrovaColors

/** How loading placeholders animate, following each platform's conventions. */
enum class SkeletonStyle {
    /** Material and web: a highlight sweeping across the placeholder shapes. */
    Shimmer,

    /** Apple's redacted placeholders: plain rounded shapes in place of the content, gently pulsing. */
    Placeholder,
}

val LocalSkeletonStyle = staticCompositionLocalOf { SkeletonStyle.Shimmer }

private val SkeletonBase = VentrovaColors.Ink.copy(alpha = 0.08f)
private val SkeletonHighlight = Color.White.copy(alpha = 0.55f)

/** Paints this element as a loading placeholder in the current [LocalSkeletonStyle]. */
fun Modifier.skeleton(shape: Shape = RoundedCornerShape(6.dp)): Modifier = composed {
    val style = LocalSkeletonStyle.current
    val transition = rememberInfiniteTransition()
    when (style) {
        SkeletonStyle.Shimmer -> {
            val progress by transition.animateFloat(
                initialValue = 0f,
                targetValue = 1f,
                animationSpec = infiniteRepeatable(tween(1300, easing = LinearEasing)),
            )
            clip(shape).background(SkeletonBase).drawWithContent {
                val width = size.width
                val sweep = width * 0.6f
                val x = -sweep + (width + sweep * 2) * progress
                drawRect(Brush.linearGradient(listOf(Color.Transparent, SkeletonHighlight, Color.Transparent), start = Offset(x - sweep, 0f), end = Offset(x + sweep, size.height)))
            }
        }
        SkeletonStyle.Placeholder -> {
            val alpha by transition.animateFloat(
                initialValue = 1f,
                targetValue = 0.55f,
                animationSpec = infiniteRepeatable(tween(900), repeatMode = RepeatMode.Reverse),
            )
            alpha(alpha).clip(shape).background(VentrovaColors.Ink.copy(alpha = 0.11f))
        }
    }
}

/** A single line of placeholder text. */
@Composable
fun SkeletonLine(width: Dp, height: Dp = 12.dp, modifier: Modifier = Modifier) {
    val corner = if (LocalSkeletonStyle.current == SkeletonStyle.Placeholder) height / 2 else 4.dp
    Box(modifier.width(width).height(height).skeleton(RoundedCornerShape(corner)))
}

/** Placeholder for a place row: thumbnail, name and two detail lines. */
@Composable
fun PlaceRowSkeleton(
    modifier: Modifier = Modifier,
    thumbSize: Dp = 58.dp,
    thumbShape: Shape = RoundedCornerShape(15.dp),
    spacing: Dp = 12.dp,
    lines: Int = 2,
    seed: Int = 0,
) {
    // Slightly different widths per row, so the list doesn't look like a grid of identical bars.
    val widths = listOf(0.62f, 0.48f, 0.7f, 0.55f)
    Row(modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(spacing)) {
        Box(Modifier.size(thumbSize).skeleton(thumbShape))
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
            SkeletonLine(width = (90 + 140 * widths[seed % widths.size]).dp, height = 14.dp)
            repeat(lines) { line ->
                SkeletonLine(width = (70 + 140 * widths[(seed + line + 1) % widths.size]).dp, height = 10.dp)
            }
        }
    }
}

/** A short list of [PlaceRowSkeleton]s, announced once to screen readers. */
@Composable
fun PlaceListSkeleton(
    count: Int = 5,
    modifier: Modifier = Modifier,
    rowPadding: Dp = 10.dp,
    loadingLabel: String,
    row: @Composable (index: Int) -> Unit = { index -> PlaceRowSkeleton(Modifier.padding(rowPadding), seed = index) },
) {
    Column(modifier.semantics { contentDescription = loadingLabel }) {
        repeat(count) { row(it) }
    }
}
